import React, { FC, SyntheticEvent, useCallback } from "react";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import {
  Drawer,
  DrawerProps,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Divider,
  Switch,
  useTheme,
  Typography,
  Button,
  ListItemText,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useAuthContext } from "context/AuthContext";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import {
  BookmarkIcon,
  ExploreIcon,
  HomeIcon,
  UsersIcon,
  LogOutIcon,
  TermsIcon,
  PolicyIcon,
} from "icons";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import { NavLink, useHistory } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import BlockIcon from "@mui/icons-material/Block";
import SideMenuProfile from "components/SideMenuProfile";
import { ColorModeContext } from "Theme";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { useProductTour } from "providers/ProductTour";
import ProductTourIcon from "assets/product-tour.svg";
import { useRefer } from "components/refer";
import imgRefer from "assets/image/refer.webp";
import RefreshIcon from "@mui/icons-material/Refresh";
import { MY_ACCOUNT_URL, REGULATORY_URL } from "config";
import { ManageAccounts, Settings } from "@mui/icons-material";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useAccountSyncMutation } from "api";
import { accountSync } from "../accountSync";
import toast from "react-hot-toast";
import ReactGA from "react-ga4";
import { CLICK_MENU_MODULE, USER_LOG_OUT } from "utills/events";
import styled from "styled-components";
import { isDarkMode } from "utills";
import AccountSettingsIcon from "assets/icons/svg/support-2.svg";

interface SideMenuProps extends DrawerProps {
  handleCloseSideMenu: () => void;
}

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    boxSizing: "border-box",
  },
}));
const SideMenu: FC<SideMenuProps> = ({ handleCloseSideMenu, ...rest }) => {
  const auth = useAuth();
  const theme: any = useTheme();
  const [accSync] = useAccountSyncMutation();
  const colorMode = React.useContext(ColorModeContext);
  const { handleModalOpen } = useProductTour();
  const { handleOpenReferDrawer } = useRefer();
  const history = useHistory();
  const client = useApolloClient();
  const { user, removeUserInfo } = useAuthContext();

  const handleLogout = useCallback(
    async (event: SyntheticEvent) => {
      event.preventDefault();
      ReactGA.event(USER_LOG_OUT, {});
      auth.signoutRedirect({
        id_token_hint: auth.user?.id_token,
      });
      handleCloseSideMenu();
      client.resetStore();
      removeUserInfo();
      /* Track a custom event */
      (window as any)?.dataLayer?.push({ event: "Logout" });
      history.replace("/");
    },
    [client, history]
  );

  const handleRefersh = async () => {
    try {
      handleCloseSideMenu();
      const user = await auth.signinSilent();
      if (user?.access_token) {
        const { data } = await accountSync(user?.access_token);
        if (data?.data?.accountSync_V2?.userDetails?.canBeOnboarded) {
          history.replace(`/onboarding`);
        } else {
          (window as Window).location = window.location.href;
        }
        // await client.resetStore();
        // toast.success("Account sync");
      }
    } catch (e: any) {
      toast.error(e.resposne);
    }
  };

  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(!open);
  };
  const [openPolicies, setOpenPolicies] = React.useState(false);

  const handleClickPloicies = () => {
    setOpenPolicies(!openPolicies);
  };
  return (
    <Drawer
      anchor="left"
      sx={{
        width: "min(80%, 280px)",
      }}
      PaperProps={{
        sx: {
          width: "inherit",
          ".MuiListItemText-primary": {
            fontSize: "0.875rem",
            lineHeight: "1.25",
          },
          ".MuiListItemIcon-root": {
            minWidth: "36px",
          },
        },
      }}
      {...rest}
    >
      <Stack sx={{ flex: 1 }}>
        <SideMenuProfile onClick={handleCloseSideMenu} />
        <Divider />
        <Stack sx={{ flex: 1 }}>
          <SimpleBar style={{ maxHeight: "calc(100vh - calc(2 * 132px))" }}>
            <List>
              <ListItemButton
                component="a"
                style={{ display: 'flex' }}
                href={MY_ACCOUNT_URL}
                target="__target"
                onClick={() => {
                  ReactGA.event(CLICK_MENU_MODULE, {
                    event_label: "manage your account",
                  });
                  handleCloseSideMenu();
                }}
              >
                <ListItemIcon>
                  <ManageAccounts fontSize="small" />
                </ListItemIcon>
                <ListItemText>Manage Your Account</ListItemText>
              </ListItemButton>
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/explore"
                  activeClassName="active"
                  exact
                  onClick={() => {
                    ReactGA.event(CLICK_MENU_MODULE, {
                      event_label: "explore",
                    });
                    handleCloseSideMenu();
                  }}
                >
                  <ListItemIcon>
                    <ExploreIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Explore" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={`/clubs`}
                  activeClassName="active"
                  exact
                  onClick={() => {
                    ReactGA.event(CLICK_MENU_MODULE, {
                      event_label: "clubs",
                    });
                    handleCloseSideMenu();
                  }}
                >
                  <ListItemIcon>
                    <UsersIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Clubs" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/bookmarks"
                  activeClassName="active"
                  onClick={() => {
                    ReactGA.event(CLICK_MENU_MODULE, {
                      event_label: "bookmarks",
                    });
                    handleCloseSideMenu();
                  }}
                >
                  <ListItemIcon>
                    <BookmarkIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Bookmarks" />
                </ListItemButton>
              </ListItem>
              <ListItemButton
                component={NavLink}
                to="/settings/blocked-users"
                activeClassName="active"
                onClick={() => {
                  ReactGA.event(CLICK_MENU_MODULE, {
                    event_label: "blocked users",
                  });
                  handleCloseSideMenu();
                }}
              >
                <ListItemIcon>
                  <BlockIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Blocked Users"
                  sx={{
                    ".MuiListItemText-primary": {
                      fontSize: "0.8125rem",
                    },
                  }}
                />
              </ListItemButton>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    ReactGA.event(CLICK_MENU_MODULE, {
                      event_label: "refer and earn",
                    });
                    handleOpenReferDrawer?.();
                    handleCloseSideMenu();
                  }}
                >
                  <ListItemIcon>
                    <img
                      src={imgRefer}
                      alt=""
                      style={{
                        height: "24px",
                        width: "24px",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText>Refer and Earn</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/settings-support"
                  activeClassName="active"
                  onClick={() => {
                    handleCloseSideMenu();
                  }}
                >
                  <ListItemIcon>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText>Account Settings</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {theme.palette.mode === "dark" ? (
                    <Brightness7Icon fontSize="small" />
                  ) : (
                    <Brightness4Icon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={`Dark Mode ${isDarkMode(theme) ? "on" : "off"}`}
                  sx={{
                    ".MuiListItemText-primary": {
                      fontSize: "0.8125rem",
                    },
                  }}
                />
                <Switch
                  size="small"
                  checked={theme.palette.mode === "dark"}
                  onChange={colorMode.toggleColorMode}
                  inputProps={{ "aria-label": "controlled" }}
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleClick}
                >
                  <ListItemIcon>
                    <SettingsOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Support" />
                  {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    component={NavLink}
                    to="/help-centre"
                    activeClassName="active"
                    onClick={() => {
                      ReactGA.event(CLICK_MENU_MODULE, {
                        event_label: "help centre",
                      });
                      handleCloseSideMenu();
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
                      </svg>
                    </ListItemIcon>
                    <ListItemText
                      primary="Help Centre"
                      sx={{
                        ".MuiListItemText-primary": {
                          fontSize: "0.8125rem",
                        },
                      }}
                    />
                  </ListItemButton>
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => {
                      handleModalOpen?.();
                      ReactGA.event(CLICK_MENU_MODULE, {
                        event_label: "product tour",
                      });
                      handleCloseSideMenu();
                    }}
                  >
                    <ListItemIcon>
                      <ProductTourIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Product Tour"
                      sx={{
                        ".MuiListItemText-primary": {
                          fontSize: "0.8125rem",
                        },
                      }}
                    />
                  </ListItemButton>
                </List>
              </Collapse>
              <ListItem disablePadding>
                <ListItemButton onClick={handleClickPloicies}>
                  <ListItemIcon>
                    <InventoryOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Policies" />
                  {openPolicies ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openPolicies} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    component={NavLink}
                    to="/privacy"
                    activeClassName="active"
                    onClick={() => {
                      ReactGA.event(CLICK_MENU_MODULE, {
                        event_label: "privacy",
                      });
                      handleCloseSideMenu();
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon>
                      <PolicyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Privacy Policy"
                      sx={{
                        ".MuiListItemText-primary": {
                          fontSize: "0.8125rem",
                        },
                      }}
                    />
                  </ListItemButton>
                  <ListItemButton
                    component={NavLink}
                    to="/terms"
                    activeClassName="active"
                    onClick={() => {
                      ReactGA.event(CLICK_MENU_MODULE, {
                        event_label: "terms",
                      });
                      handleCloseSideMenu();
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon>
                      <TermsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Terms of Use"
                      sx={{
                        ".MuiListItemText-primary": {
                          fontSize: "0.8125rem",
                        },
                      }}
                    />
                  </ListItemButton>
                  <ListItemButton
                    component="a"
                    href={REGULATORY_URL}
                    target="_blank"
                    onClick={() => {
                      ReactGA.event(CLICK_MENU_MODULE, {
                        event_label: "regulatory disclosures",
                      });
                      handleCloseSideMenu();
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon>
                      <GavelOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Regulatory Disclosures"
                      sx={{
                        ".MuiListItemText-primary": {
                          fontSize: "0.8125rem",
                        },
                      }}
                    />
                  </ListItemButton>
                </List>
              </Collapse>
            </List>
            <Divider />
          </SimpleBar>
        </Stack>
        <List disablePadding>
          {/* <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogOutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem> */}
          {/* <ListItem sx={{ ".css-m754at-MuiListItemText-root ": { display: "none" } }}>
            <ListItemIcon>
              <svg
                width="20"
                height="20"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.03333 3.99997C4.1 4.09997 4.2 4.13331 4.3 4.13331C4.36667 4.13331 4.43333 4.09997 4.5 4.06664C4.63333 3.96664 4.66667 3.73331 4.56667 3.59997L4.36667 3.33331C4.26667 3.19997 4.06667 3.16664 3.9 3.26664C3.76667 3.36664 3.73333 3.59997 3.83333 3.73331L4.03333 3.99997Z"
                  fill="#717171"
                />
                <path
                  d="M2.33333 9.26664L2 9.36664C1.83333 9.43331 1.73333 9.59997 1.8 9.79997C1.83333 9.93331 1.96667 10.0333 2.13333 10.0333H2.23333L2.56667 9.93331C2.73333 9.86664 2.83333 9.69997 2.76667 9.49997C2.7 9.33331 2.5 9.23331 2.33333 9.26664Z"
                  fill="#717171"
                />
                <path
                  d="M7.36667 3.16663C7.56667 3.16663 7.7 3.03329 7.7 2.83329V2.49996C7.7 2.29996 7.56667 2.16663 7.36667 2.16663C7.16667 2.16663 7.03333 2.29996 7.03333 2.49996V2.83329C7.03333 3.03329 7.16667 3.16663 7.36667 3.16663Z"
                  fill="#717171"
                />
                <path
                  d="M2.03333 6.63334C2.03333 6.63334 2.43333 6.76667 2.46667 6.76667C2.6 6.76667 2.73333 6.66667 2.8 6.53334C2.86667 6.36667 2.76667 6.16667 2.6 6.10001L2.26667 6.00001C2.1 5.93334 1.9 6.03334 1.83333 6.20001C1.73333 6.36667 1.83333 6.56667 2.03333 6.63334Z"
                  fill="#717171"
                />
                <path
                  d="M4.03333 12L3.83333 12.2667C3.73333 12.4 3.76667 12.6333 3.9 12.7333C3.96667 12.7667 4.03333 12.8 4.1 12.8C4.2 12.8 4.3 12.7667 4.36667 12.6667L4.56667 12.4C4.66667 12.2667 4.63333 12.0333 4.5 11.9333C4.36667 11.8 4.16667 11.8333 4.03333 12Z"
                  fill="#717171"
                />
                <path
                  d="M14.1 7.39998C14 7.33332 13.8667 7.29998 13.7667 7.33332C13.4667 7.43332 13.1333 7.49998 12.8 7.43332C11.7333 7.26665 10.9667 6.26665 11.1333 5.19998C11.2 4.79998 11.3667 4.43332 11.6667 4.13332C11.7667 4.03332 11.7667 3.89998 11.7333 3.79998C11.7 3.66665 11.5667 3.59998 11.4 3.56665C10.6 3.43332 9.8 3.63332 9.13333 4.03332C8.6 3.79998 8 3.66665 7.4 3.66665C5 3.63332 3.03333 5.59998 3.03333 7.99998C3.03333 10.4 5 12.3666 7.4 12.3666C8.9 12.3666 10.3 11.6 11.1 10.3333C12.5667 10.2666 13.8667 9.23332 14.2333 7.73332C14.2333 7.59998 14.2 7.46665 14.1 7.39998ZM7.4 11.7C5.36667 11.7 3.7 10.0333 3.7 7.99998C3.7 5.96665 5.36667 4.29998 7.4 4.29998C7.8 4.29998 8.16667 4.36665 8.53333 4.46665C8 4.96665 7.66667 5.63332 7.53333 6.39998C7.26667 8.23332 8.5 9.93332 10.3 10.2333C9.6 11.1666 8.53333 11.7 7.4 11.7ZM10.5 9.63332C9 9.39998 7.96667 8.03332 8.16667 6.53332C8.36667 5.19998 9.46667 4.23332 10.7667 4.19998C10.6 4.46665 10.5 4.79998 10.4667 5.13332C10.2667 6.56665 11.2667 7.89998 12.7 8.13332C12.9333 8.16665 13.1333 8.16665 13.3667 8.13332C12.8667 9.16665 11.7 9.79998 10.5 9.63332Z"
                  fill="#717171"
                />
                <path
                  d="M10.6667 11.9999C10.5667 11.8666 10.3667 11.8333 10.2 11.9333C10.0667 12.0333 10.0333 12.2666 10.1333 12.3999L10.3333 12.6666C10.4 12.7666 10.5 12.7999 10.6 12.7999C10.6667 12.7999 10.7333 12.7666 10.8 12.7333C10.9333 12.6333 10.9667 12.3999 10.8667 12.2666L10.6667 11.9999Z"
                  fill="#717171"
                />
                <path
                  d="M7.36667 12.8333C7.16667 12.8333 7.03333 12.9666 7.03333 13.1666V13.4999C7.03333 13.6999 7.16667 13.8333 7.36667 13.8333C7.56667 13.8333 7.7 13.6999 7.7 13.4999V13.1666C7.7 12.9666 7.53333 12.8333 7.36667 12.8333Z"
                  fill="#717171"
                />
              </svg>
            </ListItemIcon>
            <ListItemText
              sx={{
                ".MuiListItemText-primary": {
                  fontSize: "0.8125rem",
                },
              }}
            />
            <AntSwitch
              defaultChecked
              checked={theme.palette.mode === "dark"}
              onChange={colorMode.toggleColorMode}

              inputProps={{ "aria-label": "ant design" }}
            />
          </ListItem> */}

          <ListItem>
            <Typography variant="caption" sx={{ fontSize: "0.725rem" }}>
              SEBI Registration:
              <br />
              Investment Adviser: <b>INA000017781</b>
              <br />
              Research Analyst: <b>INH300007493</b>l
            </Typography>
          </ListItem>
        </List>
      </Stack>
    </Drawer>
  );
};

export default SideMenu;
